import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import { AdminLayout } from '@/components/AdminLayout'
import { scriptsService, type Script } from '@/services/scripts.service'

export function ScriptManagementPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [editingScript, setEditingScript] = useState<Script | null>(null)
  const [viewingScript, setViewingScript] = useState<Script | null>(null)
  const [scriptContent, setScriptContent] = useState('')
  const [newVersion, setNewVersion] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['scripts'],
    queryFn: () => scriptsService.list(),
  })

  const scripts = data?.scripts || []

  const saveMutation = useMutation({
    mutationFn: async (data: { version: string; content: string; active: boolean }) => {
      const existingScript = scripts.find((s) => s.version === data.version)
      if (existingScript) {
        return scriptsService.update(data.version, {
          content: data.content,
          active: data.active,
        })
      } else {
        return scriptsService.create({
          version: data.version,
          content: data.content,
          active: data.active,
        })
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scripts'] })
      setEditingScript(null)
      setScriptContent('')
      setNewVersion('')
    },
  })

  const loadScriptMutation = useMutation({
    mutationFn: async (version: string) => {
      const result = await scriptsService.get(version)
      return result.script
    },
    onSuccess: (script) => {
      setEditingScript(script)
      setScriptContent(script.content || '')
      setNewVersion(script.version)
      setViewingScript(null)
    },
  })

  const viewScriptMutation = useMutation({
    mutationFn: async (version: string) => {
      const result = await scriptsService.get(version)
      return result.script
    },
    onSuccess: (script) => {
      setViewingScript(script)
      setEditingScript(null)
    },
  })

  const handleEdit = (script: Script) => {
    loadScriptMutation.mutate(script.version)
  }

  const handleView = (script: Script) => {
    viewScriptMutation.mutate(script.version)
  }

  const handleSave = () => {
    if (!newVersion || !scriptContent) return
    saveMutation.mutate({
      version: newVersion,
      content: scriptContent,
      active: editingScript?.active || false,
    })
  }

  const handleCreateNew = () => {
    let nextVersion = 'v1.0'
    if (scripts.length > 0) {
      const latestVersion = scripts[0].version.replace(/^v/, '')
      const parts = latestVersion.split('.')
      const major = parseInt(parts[0] || '1', 10)
      const minor = parseInt(parts[1] || '0', 10)
      nextVersion = `v${major}.${minor + 1}`
    }
    setEditingScript({ version: nextVersion, content: '', updatedAt: '', active: true })
    setNewVersion(nextVersion)
    setScriptContent('')
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('admin.scriptManagement')}</h1>
            <p className="text-gray-600">{t('admin.scriptManagementDesc')}</p>
          </div>
          <button
            onClick={handleCreateNew}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            {t('admin.createScript')}
          </button>
        </div>

        {isLoading ? (
          <div className="text-center py-8 text-gray-600">{t('admin.loadingScripts')}</div>
        ) : viewingScript ? (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">
                {t('common.version')} {viewingScript.version}
              </h2>
              <button
                onClick={() => setViewingScript(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                {t('common.back')}
              </button>
            </div>

            <div className="prose prose-slate max-w-none">
              <div className="bg-white rounded-md p-6 border border-gray-200">
                <ReactMarkdown
                  components={{
                    h1: ({ node, ...props }) => (
                      <h1 className="text-2xl font-bold text-gray-900 mb-4 mt-6" {...props} />
                    ),
                    h2: ({ node, ...props }) => (
                      <h2 className="text-xl font-bold text-gray-900 mb-3 mt-5" {...props} />
                    ),
                    h3: ({ node, ...props }) => (
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 mt-4" {...props} />
                    ),
                    p: ({ node, ...props }) => (
                      <p className="text-gray-700 mb-3 leading-relaxed" {...props} />
                    ),
                    ul: ({ node, ...props }) => (
                      <ul className="list-disc list-inside mb-3 space-y-1" {...props} />
                    ),
                    ol: ({ node, ...props }) => (
                      <ol className="list-decimal list-inside mb-3 space-y-1" {...props} />
                    ),
                    li: ({ node, ...props }) => (
                      <li className="text-gray-700" {...props} />
                    ),
                    strong: ({ node, ...props }) => (
                      <strong className="font-bold text-gray-900" {...props} />
                    ),
                    em: ({ node, ...props }) => (
                      <em className="italic text-gray-800" {...props} />
                    ),
                    blockquote: ({ node, ...props }) => (
                      <blockquote className="border-l-4 border-blue-500 pl-4 py-2 mb-3 italic text-gray-600" {...props} />
                    ),
                    code: ({ node, inline, ...props }: any) =>
                      inline ? (
                        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono text-gray-800" {...props} />
                      ) : (
                        <code className="block bg-gray-100 p-3 rounded text-sm font-mono text-gray-800 mb-3" {...props} />
                      ),
                  }}
                >
                  {viewingScript.content || ''}
                </ReactMarkdown>
              </div>
            </div>

            <div className="mt-6 flex items-center space-x-4">
              <div className="text-sm text-gray-600">
                <span className="font-medium">{t('common.status')}:</span>{' '}
                {viewingScript.active ? (
                  <span className="text-green-600">{t('common.active')}</span>
                ) : (
                  <span className="text-gray-500">{t('common.inactive')}</span>
                )}
              </div>
              <div className="text-sm text-gray-600">
                <span className="font-medium">{t('common.updated')}:</span>{' '}
                {new Date(viewingScript.updatedAt).toLocaleDateString('de-DE')}
              </div>
            </div>
          </div>
        ) : editingScript ? (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">
              {t('admin.editScript', { version: newVersion })}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('common.version')}
                </label>
                <input
                  type="text"
                  value={newVersion}
                  onChange={(e) => setNewVersion(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="z.B. 1.0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.scriptContent')}
                </label>
                <textarea
                  value={scriptContent}
                  onChange={(e) => setScriptContent(e.target.value)}
                  rows={30}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-base leading-relaxed"
                  placeholder={t('admin.scriptContentPlaceholder')}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t('admin.markdownSupported')}
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleSave}
                  disabled={saveMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300"
                >
                  {saveMutation.isPending ? t('common.saving') : t('common.save')}
                </button>
                <button
                  onClick={() => {
                    setEditingScript(null)
                    setScriptContent('')
                    setNewVersion('')
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('common.version')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('common.status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('common.updated')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {scripts.map((script) => (
                  <tr key={script.version}>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {script.version}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {script.active ? (
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                          {t('common.active')}
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs">
                          {t('common.inactive')}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(script.updatedAt).toLocaleDateString('de-DE')}
                    </td>
                    <td className="px-6 py-4 text-right text-sm space-x-2">
                      <button
                        onClick={() => handleView(script)}
                        className="text-gray-600 hover:text-gray-700"
                      >
                        {t('common.view')}
                      </button>
                      <button
                        onClick={() => handleEdit(script)}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        {t('common.edit')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  )
}
